import { lazy, Suspense } from "react";
import { restaurantSlugFromHostname } from "./publicHostname";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import type { UserRole } from "@restlab/shared";
import { useAuth } from "./auth/AuthProvider";
import { AppShell } from "./components/AppShell";
const LoginPage = lazy(() =>
  import("./pages/LoginPage").then((m) => ({ default: m.LoginPage })),
);
const DashboardPage = lazy(() =>
  import("./pages/DashboardPage").then((m) => ({ default: m.DashboardPage })),
);
const RestaurantDashboardPage = lazy(() =>
  import("./pages/RestaurantDashboardPage").then((m) => ({
    default: m.RestaurantDashboardPage,
  })),
);
const RestaurantStatisticsPage = lazy(() =>
  import("./pages/RestaurantStatisticsPage").then((m) => ({
    default: m.RestaurantStatisticsPage,
  })),
);
const DailyBookPage = lazy(() =>
  import("./pages/DailyBookPage").then((m) => ({ default: m.DailyBookPage })),
);
const StandardsPage = lazy(() =>
  import("./pages/StandardsPage").then((m) => ({ default: m.StandardsPage })),
);
const CalendarPage = lazy(() =>
  import("./pages/CalendarPage").then((m) => ({ default: m.CalendarPage })),
);
const WaitlistPage = lazy(() =>
  import("./pages/WaitlistPage").then((m) => ({ default: m.WaitlistPage })),
);
const MigrationPage = lazy(() =>
  import("./pages/MigrationPage").then((m) => ({ default: m.MigrationPage })),
);
const AlertsPage = lazy(() =>
  import("./pages/AlertsPage").then((m) => ({ default: m.AlertsPage })),
);
const PublicBookingPage = lazy(() =>
  import("./pages/PublicBookingPage").then((m) => ({
    default: m.PublicBookingPage,
  })),
);
const AccessPage = lazy(() =>
  import("./pages/AccessPage").then((m) => ({ default: m.AccessPage })),
);
const ManageReservationPage = lazy(() =>
  import("./pages/ManageReservationPage").then((m) => ({
    default: m.ManageReservationPage,
  })),
);
const AdminPage = lazy(() =>
  import("./pages/AdminPage").then((m) => ({ default: m.AdminPage })),
);
const AdminPlansPage = lazy(() =>
  import("./pages/AdminPlansPage").then((m) => ({ default: m.AdminPlansPage })),
);
const AdminEmailPage = lazy(() =>
  import("./pages/AdminEmailPage").then((m) => ({ default: m.AdminEmailPage })),
);
const AdminSmsPage = lazy(() =>
  import("./pages/AdminSmsPage").then((m) => ({ default: m.AdminSmsPage })),
);
const AdminBillingPage = lazy(() =>
  import("./pages/AdminBillingPage").then((m) => ({
    default: m.AdminBillingPage,
  })),
);
const BillingPage = lazy(() =>
  import("./pages/BillingPage").then((m) => ({ default: m.BillingPage })),
);
import { AdminShell } from "./components/AdminShell";
const FloorPlanPage = lazy(() =>
  import("./pages/FloorPlanPage").then((m) => ({ default: m.FloorPlanPage })),
);
const CustomersPage = lazy(() =>
  import("./pages/CustomersPage").then((m) => ({ default: m.CustomersPage })),
);
const RestaurantSettingsPage = lazy(() =>
  import("./pages/RestaurantSettingsPage").then((m) => ({
    default: m.RestaurantSettingsPage,
  })),
);
const RestaurantTeamPage = lazy(() =>
  import("./pages/RestaurantTeamPage").then((m) => ({
    default: m.RestaurantTeamPage,
  })),
);
import { landingPathFor } from "./auth/landingPath";
const ChangePasswordPage = lazy(() =>
  import("./pages/ChangePasswordPage").then((m) => ({
    default: m.ChangePasswordPage,
  })),
);
const GroupUsersPage = lazy(() =>
  import("./pages/GroupUsersPage").then((m) => ({ default: m.GroupUsersPage })),
);
const PublicLegalPage = lazy(() =>
  import("./pages/PublicLegalPage").then((m) => ({
    default: m.PublicLegalPage,
  })),
);
const PublicReviewPage = lazy(() =>
  import("./pages/PublicReviewPage").then((m) => ({
    default: m.PublicReviewPage,
  })),
);
const RestaurantReviewsPage = lazy(() =>
  import("./pages/RestaurantReviewsPage").then((m) => ({
    default: m.RestaurantReviewsPage,
  })),
);
const AccountSettingsPage = lazy(() =>
  import("./pages/AccountSettingsPage").then((m) => ({
    default: m.AccountSettingsPage,
  })),
);
const AppPrivacyPage = lazy(() =>
  import("./pages/AppPrivacyPage").then((m) => ({ default: m.AppPrivacyPage })),
);
const PublicAccountDeletionPage = lazy(() =>
  import("./pages/PublicAccountDeletionPage").then((m) => ({
    default: m.PublicAccountDeletionPage,
  })),
);
import { ConnectionBanner } from "./components/ConnectionBanner";
const AppSupportPage = lazy(() =>
  import("./pages/AppSupportPage").then((m) => ({ default: m.AppSupportPage })),
);
import { DrawerDismissController } from "./components/DrawerDismissController";
import { RestaurantOnboardingGate } from "./components/RestaurantOnboardingGate";
const RestaurantOnboardingPage = lazy(() =>
  import("./pages/RestaurantOnboardingPage").then((m) => ({
    default: m.RestaurantOnboardingPage,
  })),
);
const PublicSubscriptionPage = lazy(() =>
  import("./pages/PublicSubscriptionPage").then((m) => ({
    default: m.PublicSubscriptionPage,
  })),
);
const VoicePage = lazy(() =>
  import("./pages/VoicePage").then((m) => ({ default: m.VoicePage })),
);
const AdminVoicePage = lazy(() =>
  import("./pages/AdminVoicePage").then((m) => ({ default: m.AdminVoicePage })),
);
const AdminStatisticsPage = lazy(() =>
  import("./pages/AdminStatisticsPage").then((m) => ({
    default: m.AdminStatisticsPage,
  })),
);

const PasswordRecoveryPage = lazy(() =>
  import("./pages/PasswordRecoveryPage").then((m) => ({
    default: m.PasswordRecoveryPage,
  })),
);

function FullLoader() {
  return (
    <div className="full-loader">
      <span className="spinner" />
      <p>Preparando RestLab…</p>
    </div>
  );
}

function RoleLanding() {
  const { user, loading } = useAuth();
  if (restaurantSlugFromHostname())
    return <Navigate to="/es/reservar" replace />;
  if (loading) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={landingPathFor(user)} replace />;
}

function Protected({
  children,
  groupOnly = false,
  allowedRoles,
  billingOnly = false,
}: {
  children: React.ReactNode;
  groupOnly?: boolean;
  allowedRoles?: UserRole[];
  billingOnly?: boolean;
}) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullLoader />;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (user.mustChangePassword)
    return <Navigate to="/change-password" replace />;
  if (groupOnly && user.accountType === "standalone")
    return <Navigate to={landingPathFor(user)} replace />;
  if (allowedRoles && !allowedRoles.includes(user.role))
    return <Navigate to={landingPathFor(user)} replace />;
  if (
    billingOnly &&
    !["super_admin", "group_admin"].includes(user.role) &&
    !(user.role === "manager" && user.accountType === "standalone")
  )
    return <Navigate to={landingPathFor(user)} replace />;
  return (
    <RestaurantOnboardingGate>
      <AppShell>{children}</AppShell>
    </RestaurantOnboardingGate>
  );
}

function AdminProtected({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullLoader />;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (user.mustChangePassword)
    return <Navigate to="/change-password" replace />;
  if (user.role !== "super_admin") return <Navigate to="/group" replace />;
  return <AdminShell>{children}</AdminShell>;
}

function PasswordChangeProtected() {
  const { user, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.mustChangePassword)
    return <Navigate to={landingPathFor(user)} replace />;
  return <ChangePasswordPage />;
}

function AccountProtected() {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullLoader />;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (user.mustChangePassword)
    return <Navigate to="/change-password" replace />;
  return user.role === "super_admin" ? (
    <AdminShell>
      <AccountSettingsPage />
    </AdminShell>
  ) : (
    <AppShell>
      <AccountSettingsPage />
    </AppShell>
  );
}

export function App() {
  return (
    <>
      <ConnectionBanner />
      <DrawerDismissController />
      <Suspense fallback={<FullLoader />}>
        <Routes>
          <Route path="/forgot-password" element={<PasswordRecoveryPage />} />
          <Route path="/reset-password" element={<PasswordRecoveryPage />} />
          <Route path="/" element={<RoleLanding />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/app/privacidad" element={<AppPrivacyPage />} />
          <Route
            path="/app/eliminar-cuenta"
            element={<PublicAccountDeletionPage />}
          />
          <Route path="/app/soporte" element={<AppSupportPage />} />
          <Route path="/account" element={<AccountProtected />} />
          <Route
            path="/change-password"
            element={<PasswordChangeProtected />}
          />
          <Route
            path="/book/:organizationSlug/:locationSlug"
            element={<PublicBookingPage />}
          />
          <Route path="/es/reservar" element={<PublicBookingPage />} />
          <Route path="/legal/:document" element={<PublicLegalPage />} />
          <Route path="/opinion/:token" element={<PublicReviewPage />} />
          <Route
            path="/suscripcion/:token"
            element={<PublicSubscriptionPage />}
          />
          <Route
            path="/book/:organizationSlug/:locationSlug/legal/:document"
            element={<PublicLegalPage />}
          />
          <Route
            path="/reservar"
            element={<Navigate to="/es/reservar" replace />}
          />
          <Route path="/r/:token" element={<AccessPage />} />
          <Route
            path="/manage/reservation"
            element={<ManageReservationPage />}
          />
          <Route
            path="/group"
            element={
              <Protected
                groupOnly
                allowedRoles={["super_admin", "group_admin"]}
              >
                <DashboardPage />
              </Protected>
            }
          />
          <Route
            path="/admin"
            element={
              <AdminProtected>
                <AdminPage mode="overview" />
              </AdminProtected>
            }
          />
          <Route
            path="/admin/groups"
            element={
              <AdminProtected>
                <AdminPage mode="groups" />
              </AdminProtected>
            }
          />
          <Route
            path="/admin/statistics"
            element={
              <AdminProtected>
                <AdminStatisticsPage />
              </AdminProtected>
            }
          />
          <Route
            path="/admin/restaurants"
            element={
              <AdminProtected>
                <AdminPage mode="restaurants" />
              </AdminProtected>
            }
          />
          <Route
            path="/admin/plans"
            element={
              <AdminProtected>
                <AdminPlansPage />
              </AdminProtected>
            }
          />
          <Route
            path="/admin/email"
            element={
              <AdminProtected>
                <AdminEmailPage />
              </AdminProtected>
            }
          />
          <Route
            path="/admin/sms"
            element={
              <AdminProtected>
                <AdminSmsPage />
              </AdminProtected>
            }
          />
          <Route
            path="/admin/billing"
            element={
              <AdminProtected>
                <AdminBillingPage />
              </AdminProtected>
            }
          />
          <Route
            path="/admin/voice"
            element={
              <AdminProtected>
                <AdminVoicePage />
              </AdminProtected>
            }
          />
          <Route
            path="/billing"
            element={
              <Protected
                groupOnly
                allowedRoles={["super_admin", "group_admin"]}
              >
                <BillingPage />
              </Protected>
            }
          />
          <Route
            path="/group/users"
            element={
              <Protected
                groupOnly
                allowedRoles={["super_admin", "group_admin"]}
              >
                <GroupUsersPage />
              </Protected>
            }
          />
          <Route
            path="/group/standards"
            element={
              <Protected
                groupOnly
                allowedRoles={["super_admin", "group_admin"]}
              >
                <StandardsPage />
              </Protected>
            }
          />
          <Route
            path="/locations/:locationId/onboarding"
            element={
              <Protected
                allowedRoles={["super_admin", "group_admin", "manager"]}
              >
                <RestaurantOnboardingPage />
              </Protected>
            }
          />
          <Route
            path="/locations/:locationId/dashboard"
            element={
              <Protected>
                <RestaurantDashboardPage />
              </Protected>
            }
          />
          <Route
            path="/locations/:locationId/statistics"
            element={
              <Protected>
                <RestaurantStatisticsPage />
              </Protected>
            }
          />
          <Route
            path="/locations/:locationId/book"
            element={
              <Protected>
                <DailyBookPage />
              </Protected>
            }
          />
          <Route
            path="/locations/:locationId/calendar"
            element={
              <Protected>
                <CalendarPage />
              </Protected>
            }
          />
          <Route
            path="/locations/:locationId/waitlist"
            element={
              <Protected>
                <WaitlistPage />
              </Protected>
            }
          />
          <Route
            path="/locations/:locationId/floor-plan"
            element={
              <Protected
                allowedRoles={["super_admin", "group_admin", "manager"]}
              >
                <FloorPlanPage />
              </Protected>
            }
          />
          <Route
            path="/locations/:locationId/customers"
            element={
              <Protected>
                <CustomersPage />
              </Protected>
            }
          />
          <Route
            path="/locations/:locationId/reviews"
            element={
              <Protected>
                <RestaurantReviewsPage />
              </Protected>
            }
          />
          <Route
            path="/locations/:locationId/team"
            element={
              <Protected
                allowedRoles={["super_admin", "group_admin", "manager"]}
              >
                <RestaurantTeamPage />
              </Protected>
            }
          />
          <Route
            path="/locations/:locationId/settings"
            element={
              <Protected
                allowedRoles={["super_admin", "group_admin", "manager"]}
              >
                <RestaurantSettingsPage />
              </Protected>
            }
          />
          <Route
            path="/locations/:locationId/billing"
            element={
              <Protected
                allowedRoles={["super_admin", "group_admin", "manager"]}
                billingOnly
              >
                <BillingPage />
              </Protected>
            }
          />
          <Route
            path="/locations/:locationId/voice"
            element={
              <Protected
                allowedRoles={["super_admin", "group_admin", "manager"]}
              >
                <VoicePage />
              </Protected>
            }
          />
          <Route
            path="/migration"
            element={
              <Protected
                groupOnly
                allowedRoles={["super_admin", "group_admin"]}
              >
                <MigrationPage />
              </Protected>
            }
          />
          <Route
            path="/operations/alerts"
            element={
              <Protected
                groupOnly
                allowedRoles={["super_admin", "group_admin"]}
              >
                <AlertsPage />
              </Protected>
            }
          />
          <Route path="*" element={<RoleLanding />} />
        </Routes>
      </Suspense>
    </>
  );
}
